import { Check, Copy, Key, Plus } from "lucide-react"
import * as React from "react"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { DialogClose, DialogContent } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatRelative } from "@/lib/format-date"

import { ConsoleRole, ROLE_OPTIONS, type ServiceKey } from "./users-keys-data"

const PASSWORD_MIN = 8

export function RevokeKeyDialog({
  serviceKey,
  onConfirm,
}: {
  serviceKey: ServiceKey
  onConfirm: () => void
}): JSX.Element {
  const neverUsed = serviceKey.lastUsedAt === null
  return (
    <DialogContent
      title={`Revoke ${serviceKey.name}?`}
      description="The key stops working immediately and cannot be restored."
      size="sm"
      footer={
        <div className="ml-auto flex items-center gap-2">
          <DialogClose asChild>
            <Button variant="default" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button variant="destructive" size="sm" onClick={onConfirm}>
            Revoke key
          </Button>
        </div>
      }
    >
      {neverUsed ? (
        <Alert tone="neutral" title="This key has never been used">
          Revoking it should break nothing.
        </Alert>
      ) : (
        <Alert tone="warning" title={`Used ${formatRelative(serviceKey.lastUsedAt!)}`}>
          Whatever is using this key stops working immediately.
        </Alert>
      )}
    </DialogContent>
  )
}

export function RevealKeyDialog({
  plaintext,
  keyName,
  onDone,
}: {
  plaintext: string
  keyName: string
  onDone: () => void
}): JSX.Element {
  const [stored, setStored] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  const copy = React.useCallback(async () => {
    await navigator.clipboard.writeText(plaintext)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }, [plaintext])

  return (
    <DialogContent
      title={`Save your ${keyName} key`}
      description="This is the only time we can show it. If you close this without saving it, you need to create another."
      size="md"
      onEscapeKeyDown={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
      footer={
        <div className="ml-auto flex items-center gap-2">
          <Button variant="primary" size="sm" disabled={!stored} onClick={onDone}>
            Done
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
        <code className="min-w-0 flex-1 truncate font-id text-body text-text">{plaintext}</code>
        <Button
          size="sm"
          variant="default"
          leadingIcon={copied ? <Check /> : <Copy />}
          onClick={() => {
            void copy()
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>

      <p className="mt-3 text-body-sm text-text-2">
        Store it in your secret manager, not in a repository. Anyone holding this key has the same
        access as the machine you created it for.
      </p>

      <label className="mt-4 flex items-start gap-2.5 text-body-sm text-text">
        <input
          type="checkbox"
          checked={stored}
          onChange={(e) => setStored(e.currentTarget.checked)}
          className="mt-0.5 size-4 shrink-0 rounded-xs border-border accent-[color:var(--brand)]"
        />
        <span>I have stored it somewhere safe.</span>
      </label>
    </DialogContent>
  )
}

export type AddUserSubmit =
  | { email: string; name: string | null; role: ConsoleRole; kind: "invite" }
  | { email: string; name: string | null; role: ConsoleRole; kind: "password"; password: string }

export function AddUserDialog({
  onSubmit,
  isSubmitting = false,
}: {
  onSubmit: (input: AddUserSubmit) => void
  isSubmitting?: boolean
}): JSX.Element {
  const [email, setEmail] = React.useState("")
  const [name, setName] = React.useState("")
  const [password, setPassword] = React.useState("")
  // Default to CLIENT: row-scoped, sees nothing until added to an engagement. Any staff role
  // (admin/reviewer/specialist/viewer) grants read across every client's data — pick consciously.
  const [role, setRole] = React.useState<ConsoleRole>(ConsoleRole.CLIENT)
  const [sendInvite, setSendInvite] = React.useState(true)

  const emailOk = /.+@.+\..+/.test(email.trim())
  const passwordOk = sendInvite || password.length >= PASSWORD_MIN
  const canSubmit = emailOk && passwordOk && !isSubmitting

  const submit = () => {
    if (!canSubmit) return
    const base = {
      email: email.trim(),
      name: name.trim() || null,
      role,
    }
    onSubmit(sendInvite ? { ...base, kind: "invite" } : { ...base, kind: "password", password })
  }

  return (
    <DialogContent
      title="Add user"
      description={
        sendInvite
          ? "They receive an email invitation and set their own password."
          : "They receive access as soon as you save. Send them their password out of band."
      }
      size="md"
      footer={
        <>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Plus />}
            disabled={!canSubmit}
            onClick={submit}
          >
            {isSubmitting ? (sendInvite ? "Sending…" : "Adding…") : sendInvite ? "Send invite" : "Add user"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Email" required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            placeholder="person@example.com"
            autoComplete="off"
            autoFocus
          />
        </FormField>

        <FormField label="Full name" hint="Optional — shown on comments and reviews.">
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="Alex Miller"
            autoComplete="off"
          />
        </FormField>

        <FormField label="Role" required>
          <Select value={role} onValueChange={(v) => setRole(v as ConsoleRole)}>
            <SelectTrigger size="md" aria-label="Role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="w-[340px]" align="start">
              {ROLE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} description={opt.description}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <label className="flex items-start gap-2.5 text-body-sm text-text">
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.currentTarget.checked)}
            className="mt-0.5 size-4 shrink-0 rounded-xs border-border accent-[color:var(--brand)]"
          />
          <span>
            <span className="font-medium">Email an invitation</span>
            <span className="mt-0.5 block text-caption text-text-3">
              They set their own password via a signed link. Recommended.
            </span>
          </span>
        </label>

        {!sendInvite && (
          <FormField label="Password" required hint={`At least ${PASSWORD_MIN} characters.`}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="new-password"
            />
          </FormField>
        )}

        {role === ConsoleRole.CLIENT && (
          <Alert tone="neutral" size="sm">
            A client account sees nothing until you add it to an engagement on the Clients screen.
            The role alone grants no access.
          </Alert>
        )}

        {role !== ConsoleRole.CLIENT && (
          <Alert tone="warning" size="sm" title="Staff role — grants cross-client access">
            This person will be able to read every client's reviews and findings. Only pick this
            for internal PyxisRC teammates.
          </Alert>
        )}
      </div>
    </DialogContent>
  )
}

export function CreateKeyDialog({
  onSubmit,
  isSubmitting = false,
}: {
  onSubmit: (name: string) => void
  isSubmitting?: boolean
}): JSX.Element {
  const [name, setName] = React.useState("")
  const canSubmit = name.trim().length > 0 && !isSubmitting

  const submit = () => {
    if (!canSubmit) return
    onSubmit(name.trim())
  }

  return (
    <DialogContent
      title="Create service key"
      description="A machine credential for calling the API. The full key shows once — save it before closing."
      size="md"
      footer={
        <>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Key />}
            disabled={!canSubmit}
            onClick={submit}
          >
            {isSubmitting ? "Creating…" : "Create key"}
          </Button>
        </>
      }
    >
      <FormField
        label="Key name"
        required
        hint="A short label so you can tell one key from another later — e.g. ci-pipeline."
      >
        <Input
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="ci-pipeline"
          autoComplete="off"
          autoFocus
        />
      </FormField>
    </DialogContent>
  )
}

export function RemoveUserDialog({
  email,
  onConfirm,
}: {
  email: string
  onConfirm: () => void
}): JSX.Element {
  return (
    <DialogContent
      title={`Remove ${email}?`}
      description="They lose console access immediately. Any sessions they have open are ended on their next request."
      size="sm"
      footer={
        <div className="ml-auto flex items-center gap-2">
          <DialogClose asChild>
            <Button variant="default" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button variant="destructive" size="sm" onClick={onConfirm}>
            Remove account
          </Button>
        </div>
      }
    >
      <Alert tone="warning">
        A removed account cannot be restored — you would create a new one, and it would start with
        no history.
      </Alert>
    </DialogContent>
  )
}
